<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Close"
    :hideSubmitButton="true"
    :dismissible="true"
    size="xl"
    @close="reset"
  >
    <template #title>
      <span v-if="selected"
        >{{ selected.side === "buyer" ? "Buyer country" : "Supply country" }}
        {{ selected.country }}</span
      >
    </template>
    <template #body>
      <div class="p-3">
        <LoadingIndicatorComponent v-if="isLoading" message="Loading..." />
        <div v-else-if="detail">
          <p class="text-muted small mb-3">
            <template v-if="detail.side === 'buyer'">
              Every sale in the selected period whose counterparty is registered
              in {{ detail.country }}. The registry column shows where the
              volume was delivered, so a buyer coded with its registry country
              stands out.
            </template>
            <template v-else>
              Companies in {{ detail.country }} invoiced in the selected period
              (producers, plus buyers paying a procurement fee), and every sale
              that sourced volume from them.
            </template>
          </p>

          <div v-if="detail.side === 'supply'" class="mb-4">
            <h6 class="fw-bold mb-2">
              Companies ({{ detail.companies.length }})
            </h6>
            <DataTable
              :value="detail.companies"
              sortField="go_payout_paid"
              :sortOrder="-1"
              scrollable
              scrollHeight="320px"
              class="p-datatable-sm border rounded"
            >
              <Column field="company_name" header="Company" sortable>
                <template #body="{ data }">
                  <span class="badge text-bg-secondary me-1">{{
                    data.company_id
                  }}</span
                  >{{ data.company_name }}
                </template>
              </Column>
              <Column
                field="tx_count"
                header="Sales"
                sortable
                :style="{ width: '80px' }"
              />
              <Column
                field="mwh_sourced"
                header="MWh"
                sortable
                :style="{ width: '110px' }"
              >
                <template #body="{ data }">{{
                  data.mwh_sourced.toLocaleString()
                }}</template>
              </Column>
              <Column field="go_payout_paid" header="GO payout" sortable>
                <template #body="{ data }">
                  <span class="text-danger"
                    >{{ formatMoney(data.go_payout_paid) }} €</span
                  >
                  <div
                    v-if="data.go_payout_pending > 0"
                    class="small text-danger fst-italic"
                  >
                    + {{ formatMoney(data.go_payout_pending) }} € pending
                  </div>
                </template>
              </Column>
              <Column field="commission_settled" header="Commission" sortable>
                <template #body="{ data }">
                  <span class="text-primary"
                    >{{ formatMoney(data.commission_settled) }} €</span
                  >
                  <div
                    v-if="data.commission_pending > 0"
                    class="small text-primary fst-italic"
                  >
                    + {{ formatMoney(data.commission_pending) }} € pending
                  </div>
                </template>
              </Column>
              <Column
                field="platform_fees_settled"
                header="Platform fees"
                sortable
              >
                <template #body="{ data }">
                  <span class="text-success"
                    >{{ formatMoney(data.platform_fees_settled) }} €</span
                  >
                  <div
                    v-if="data.platform_fees_pending > 0"
                    class="small text-success fst-italic"
                  >
                    + {{ formatMoney(data.platform_fees_pending) }} € pending
                  </div>
                </template>
              </Column>
              <Column field="referral_bonus" header="Referral" sortable>
                <template #body="{ data }"
                  ><span class="text-purple"
                    >{{ formatMoney(data.referral_bonus) }} €</span
                  ></template
                >
              </Column>
            </DataTable>
          </div>

          <h6 class="fw-bold mb-2">Sales ({{ detail.transactions.length }})</h6>
          <DataTable
            :value="detail.transactions"
            sortField="gross_revenue"
            :sortOrder="-1"
            scrollable
            scrollHeight="420px"
            class="p-datatable-sm border rounded"
          >
            <Column field="id" header="TX" sortable :style="{ width: '90px' }">
              <template #body="{ data }">
                <a
                  :href="`/admin/transactions/${data.id}/edit`"
                  target="_blank"
                  class="badge bg-info text-decoration-none"
                  >#{{ data.id }}</a
                >
              </template>
            </Column>
            <Column field="counterparty" header="Counterparty" sortable>
              <template #body="{ data }">
                {{ data.counterparty }}
                <EurConversionBadge :conversion="data" class="ms-1" />
              </template>
            </Column>
            <Column
              field="counterparty_country"
              header="Country / registry"
              sortable
              :style="{ width: '140px' }"
            >
              <template #body="{ data }">
                {{ data.counterparty_country ?? "–" }} /
                {{ data.counterparty_registry_country ?? "–" }}
                <font-awesome-icon
                  v-if="
                    data.counterparty_registry_country &&
                    data.counterparty_country ===
                      data.counterparty_registry_country
                  "
                  icon="exclamation-triangle"
                  class="text-warning ms-1"
                  title="Buyer country equals the delivery registry: check that it is not the registry copied into the buyer's country"
                />
              </template>
            </Column>
            <Column
              field="bucket_at"
              header="Period"
              sortable
              :style="{ width: '110px' }"
            >
              <template #body="{ data }">{{
                data.bucket_at ? data.bucket_at.slice(0, 10) : "–"
              }}</template>
            </Column>
            <Column
              field="status"
              header="Status"
              sortable
              :style="{ width: '150px' }"
            >
              <template #body="{ data }"
                ><span class="badge bg-secondary">{{
                  data.status
                }}</span></template
              >
            </Column>
            <Column
              field="mwh"
              header="MWh"
              sortable
              :style="{ width: '100px' }"
            >
              <template #body="{ data }">{{
                data.mwh.toLocaleString()
              }}</template>
            </Column>
            <Column field="gross_revenue" header="Gross" sortable>
              <template #body="{ data }">
                <span class="text-success"
                  >{{ formatMoney(data.gross_revenue_settled) }} €</span
                >
                <div
                  v-if="data.gross_revenue_pending"
                  class="small text-success fst-italic"
                >
                  + {{ formatMoney(data.gross_revenue_pending) }} € pending
                </div>
              </template>
            </Column>
            <Column field="costs_paid" header="Payout" sortable>
              <template #body="{ data }">
                <span class="text-danger"
                  >{{ formatMoney(data.costs_paid) }} €</span
                >
                <div
                  v-if="data.costs_pending"
                  class="small text-danger fst-italic"
                >
                  + {{ formatMoney(data.costs_pending) }} € pending
                </div>
              </template>
            </Column>
            <Column field="net_revenue" header="Net" sortable>
              <template #body="{ data }"
                ><span class="fw-semibold text-info"
                  >{{ formatMoney(data.net_revenue) }} €</span
                ></template
              >
            </Column>
            <Column field="expected_referral" header="Referral" sortable>
              <template #body="{ data }"
                ><span class="text-purple"
                  >{{ formatMoney(data.expected_referral) }} €</span
                ></template
              >
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-secondary" @click="modalRef?.toggleModal()">
        Close
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import EurConversionBadge from "@/money/EurConversionBadge.vue";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { ref, useTemplateRef } from "vue";
import { financialDashboardApi } from "./financialDashboardApi";
import type {
  CountryDetail,
  CountrySide,
  DashboardFilters,
} from "./financialDashboardTypes";
import { useLatestDetail } from "./useLatestDetail";

/**
 * Audit view behind one row of the country tab. Owns its own fetch; open with `open(side, country)`.
 * `filters` must be the applied filters so the modal reconciles with the tab.
 */
const props = defineProps<{
  filters: DashboardFilters;
}>();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const selected = ref<{ side: CountrySide; country: string } | null>(null);
const { detail, isLoading, load } = useLatestDetail<CountryDetail>();

function open(side: CountrySide, country: string) {
  selected.value = { side, country };
  modalRef.value?.toggleModal();
  return load(() =>
    financialDashboardApi.getCountryDetail(side, country, props.filters)
  );
}
function reset() {
  selected.value = null;
  detail.value = null;
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.text-purple {
  color: #7c3aed;
}
</style>
